// Incident detail. Shows current state, timers, action buttons (Ack /
// Resolve), and the append-only event log.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { ArrowLeft, Check, AlertCircle, CheckCircle2, Clock, ExternalLink, Workflow } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import {
  orgIncidentDoc,
  orgIncidentEventsCol,
} from '../lib/firestore';
import {
  PRIORITY_TONES,
  STATUS_LABELS,
  STATUS_TONES,
  acknowledgeIncident,
  readEvent,
  readIncident,
  resolveIncident,
} from '../lib/incidents';
import { PROVIDER_LABELS } from '../lib/integrations';
import type { Incident, IncidentEvent, IncidentEventType, IntegrationProvider } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function IncidentDetailPage() {
  const { id: incidentId } = useParams<{ id: string }>();
  const { orgId } = useOrg();

  const [incident, setIncident] = useState<Incident | null | 'missing'>(null);
  const [events, setEvents] = useState<IncidentEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'ack' | 'resolve' | null>(null);
  // 1s tick so anything reading Date.now() (response time, escalation
  // countdown) actually ticks. Stops when the page unmounts.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!orgId || !incidentId) return;
    const unsub = onSnapshot(
      orgIncidentDoc(orgId, incidentId),
      (snap) => {
        if (!snap.exists()) {
          setIncident('missing');
          return;
        }
        setIncident(readIncident(snap.data() as Parameters<typeof readIncident>[0]));
      },
      (err) => {
        console.error('[incident] snapshot failed', err);
        setError('Could not load incident.');
      },
    );
    return () => unsub();
  }, [orgId, incidentId]);

  useEffect(() => {
    if (!orgId || !incidentId) return;
    const unsub = onSnapshot(
      query(orgIncidentEventsCol(orgId, incidentId), orderBy('at', 'asc')),
      (snap) => {
        const mapped = snap.docs.map((d) =>
          readEvent(d.data() as Parameters<typeof readEvent>[0]),
        );
        // Tiebreak events that share the exact same server-timestamp.
        // The trigger transaction writes two events atomically — the
        // 'triggered' event and the step-0 'escalation_step_fired' —
        // both with FieldValue.serverTimestamp() resolving to the same
        // ms. Firestore breaks the orderBy tie by document ID, which
        // is random, so the activity log can show the escalation fire
        // before the trigger that caused it. Stable-sort by a known
        // type priority makes the ordering deterministic. Array.sort
        // is stable in ES2019+ so events with identical (at, type)
        // keep their document order.
        mapped.sort((a, b) => {
          if (a.at !== b.at) return a.at - b.at;
          return eventTypeRank(a.type) - eventTypeRank(b.type);
        });
        setEvents(mapped);
      },
      (err) => {
        console.error('[incident] events snapshot failed', err);
      },
    );
    return () => unsub();
  }, [orgId, incidentId]);

  const responseSeconds = useMemo(() => {
    if (!incident || incident === 'missing') return null;
    const end = incident.acknowledgedAt ?? Date.now();
    return Math.max(0, Math.floor((end - incident.raisedAt) / 1000));
    // tick dep keeps the live counter ticking; intentionally unused
    // in the body otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident, tick]);

  const resolutionSeconds = useMemo(() => {
    if (!incident || incident === 'missing') return null;
    const end = incident.resolvedAt ?? Date.now();
    return Math.max(0, Math.floor((end - incident.raisedAt) / 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident, tick]);

  if (!orgId || !incidentId) return null;

  if (incident === null) {
    return (
      <AppShell>
        <Container>
          <Row gap={2} align="center">
            <Spinner /> <span className="ds-text-muted">Loading…</span>
          </Row>
        </Container>
      </AppShell>
    );
  }

  if (incident === 'missing') {
    return (
      <AppShell>
        <Container>
          <Stack gap={4}>
            <ErrorBanner message="Incident not found." />
            <Link to="/incidents" className="ds-button ds-button--secondary">
              <ArrowLeft size={14} strokeWidth={2} />
              Back to incidents
            </Link>
          </Stack>
        </Container>
      </AppShell>
    );
  }

  async function ack() {
    if (busy) return;
    setBusy('ack');
    setError(null);
    try {
      await acknowledgeIncident(orgId!, incidentId!);
    } catch (err) {
      console.error('[incident] ack failed', err);
      setError(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function resolve() {
    if (busy) return;
    setBusy('resolve');
    setError(null);
    try {
      await resolveIncident(orgId!, incidentId!);
    } catch (err) {
      console.error('[incident] resolve failed', err);
      setError(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppShell>
      <Container>
        <Stack gap={2}>
          <Link
            to="/incidents"
            className="ds-text-sm ds-text-muted"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ArrowLeft size={14} strokeWidth={2} />
            All incidents
          </Link>
        </Stack>

        <PageHeader
          title={incident.title}
          subtitle={
            <Row gap={2} align="center">
              <Badge tone={STATUS_TONES[incident.status]} dot>
                {STATUS_LABELS[incident.status]}
              </Badge>
              <Badge tone={PRIORITY_TONES[incident.priority]}>{incident.priority}</Badge>
              <Link to={`/services/${incident.serviceId}`} className="ds-text-sm ds-text-muted">
                {incident.serviceName}
              </Link>
            </Row>
          }
          actions={
            <Row gap={2}>
              {incident.sourceExternalUrl && (
                // Surfaced prominently next to Ack/Resolve so engineers
                // can pivot straight to the source alert in Datadog,
                // Sentry, etc. without breaking flow. target="_blank"
                // + rel="noreferrer" because we don't trust provider-
                // supplied URLs.
                <a
                  href={incident.sourceExternalUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="ds-button ds-button--secondary"
                >
                  <ExternalLink size={14} strokeWidth={2} />
                  Open in {sourceLabel(incident.source)}
                </a>
              )}
              {incident.status === 'triggered' && (
                <Button
                  variant="primary"
                  onClick={ack}
                  loading={busy === 'ack'}
                  disabled={busy !== null}
                  iconLeft={busy === 'ack' ? undefined : <Check size={14} strokeWidth={2} />}
                >
                  Acknowledge
                </Button>
              )}
              {incident.status !== 'resolved' && (
                <Button
                  variant={incident.status === 'acknowledged' ? 'primary' : 'secondary'}
                  onClick={resolve}
                  loading={busy === 'resolve'}
                  disabled={busy !== null}
                  iconLeft={busy === 'resolve' ? undefined : <CheckCircle2 size={14} strokeWidth={2} />}
                >
                  Resolve
                </Button>
              )}
            </Row>
          }
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          {/* Summary card */}
          <Card>
            <CardBody>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 'var(--space-6)',
                }}
              >
                <Stat label="Raised by" value={incident.raisedByName ?? '—'} sub={formatTime(incident.raisedAt)} />
                <Stat
                  label="Response time"
                  value={fmtDuration(responseSeconds)}
                  sub={incident.acknowledgedAt ? 'final' : 'live'}
                  live={!incident.acknowledgedAt}
                />
                <Stat
                  label="Resolution time"
                  value={fmtDuration(resolutionSeconds)}
                  sub={incident.resolvedAt ? 'final' : 'live'}
                  live={!incident.resolvedAt}
                />
              </div>
              {incident.description && (
                <Stack gap={1} className="ds-mt-4">
                  <span className="ds-label">Description</span>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{incident.description}</p>
                </Stack>
              )}
            </CardBody>
          </Card>

          {/* Escalation card — only renders when a policy was attached. */}
          <EscalationCard incident={incident} events={events ?? []} />

          {/* Event log */}
          <Stack gap={3}>
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>Activity</h2>
            {events === null ? (
              <Row gap={2} align="center">
                <Spinner size="sm" />
                <span className="ds-text-muted ds-text-sm">Loading events…</span>
              </Row>
            ) : events.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="ds-text-muted">No events recorded yet.</p>
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardBody>
                  <ol
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-4)',
                    }}
                  >
                    {events.map((e) => (
                      <EventRow key={e.id} event={e} />
                    ))}
                  </ol>
                </CardBody>
              </Card>
            )}
          </Stack>
        </Stack>
      </Container>
    </AppShell>
  );
}

function Stat({
  label,
  value,
  sub,
  live = false,
}: {
  label: string;
  value: string;
  sub?: string;
  live?: boolean;
}) {
  return (
    <Stack gap={1}>
      <span className="ds-label">{label}</span>
      <Row gap={2} align="center">
        <span style={{ fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-semibold)' }}>{value}</span>
        {live && <Badge tone="warning" dot>Live</Badge>}
      </Row>
      {sub && <span className="ds-text-tertiary ds-text-xs">{sub}</span>}
    </Stack>
  );
}

function EventRow({ event }: { event: IncidentEvent }) {
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          display: 'grid',
          placeItems: 'center',
          background: eventToneBg(event.type),
          color: eventToneFg(event.type),
          borderRadius: 'var(--radius-full)',
        }}
      >
        {iconFor(event.type)}
      </div>
      <Stack gap={1}>
        <span style={{ fontSize: 'var(--fs-sm)' }}>
          <strong>{event.actorName}</strong> {phraseFor(event.type)}
        </span>
      </Stack>
      <span className="ds-text-tertiary ds-text-xs ds-mono">{formatTime(event.at)}</span>
    </li>
  );
}

/**
 * Causal ordering of event types within the same server-timestamp.
 * Lifecycle changes (triggered / acknowledged / resolved) come before
 * the consequences they cause (escalation_step_fired, future
 * notification_dispatched, etc.). Anything we haven't ranked falls to
 * the end.
 */
function eventTypeRank(type: IncidentEventType): number {
  switch (type) {
    case 'triggered':
      return 0;
    case 'acknowledged':
      return 1;
    case 'resolved':
      return 2;
    case 'escalation_step_fired':
      return 3;
    case 'comment':
      return 4;
    case 'note':
      return 5;
    default:
      return 99;
  }
}

function iconFor(type: IncidentEvent['type']) {
  switch (type) {
    case 'triggered':
      return <AlertCircle size={12} strokeWidth={2.25} />;
    case 'acknowledged':
      return <Check size={12} strokeWidth={2.25} />;
    case 'resolved':
      return <CheckCircle2 size={12} strokeWidth={2.25} />;
    default:
      return <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />;
  }
}
function eventToneBg(type: IncidentEvent['type']): string {
  switch (type) {
    case 'triggered':
      return 'var(--color-danger-soft)';
    case 'acknowledged':
      return 'var(--color-warning-soft)';
    case 'resolved':
      return 'var(--color-success-soft)';
    default:
      return 'var(--color-surface-hover)';
  }
}
function eventToneFg(type: IncidentEvent['type']): string {
  switch (type) {
    case 'triggered':
      return 'var(--color-danger-text)';
    case 'acknowledged':
      return 'var(--color-warning-text)';
    case 'resolved':
      return 'var(--color-success-text)';
    default:
      return 'var(--color-text-secondary)';
  }
}
function phraseFor(type: IncidentEvent['type']): string {
  switch (type) {
    case 'triggered':
      return 'triggered the incident';
    case 'acknowledged':
      return 'acknowledged the incident';
    case 'resolved':
      return 'resolved the incident';
    case 'comment':
      return 'commented';
    case 'escalation_step_fired':
      return 'received an escalation page';
    case 'note':
      return 'added a note';
    default:
      return 'updated the incident';
  }
}

function fmtDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (min < 60) return sec === 0 ? `${min}m` : `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin === 0 ? `${hr}h` : `${hr}h ${remMin}m`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Renders the escalation lifecycle for an incident — current step,
 * when the next step fires, who was paged on each fired step, and
 * who's queued up for the next step. Doesn't render at all if no
 * policy was attached (`escalationStatus === 'none'` or undefined for
 * legacy incidents).
 *
 * Reads `targets` from `escalation_step_fired` events (already in the
 * incident's events subcollection — passed in to avoid a second
 * subscription). The next step's raw targets come from the
 * `escalationSteps` snapshot on the incident itself.
 */
function EscalationCard({
  incident,
  events,
}: {
  incident: Incident;
  events: IncidentEvent[];
}) {
  const status = incident.escalationStatus;
  if (!status || status === 'none') return null;

  const stepIdx = incident.escalationStepIdx ?? -1;
  const stepCount = incident.escalationStepCount ?? 0;
  const firedCount = stepIdx >= 0 ? stepIdx + 1 : 0;
  const snapshot = incident.escalationSteps ?? [];

  const tone: 'accent' | 'success' | 'warning' | 'danger' =
    status === 'completed' ? 'success'
    : status === 'halted' ? 'warning'
    : status === 'in-progress' ? 'accent'
    : 'danger';
  const label =
    status === 'completed' ? 'All steps fired'
    : status === 'halted' ? 'Halted'
    : status === 'in-progress' ? 'In progress'
    : 'Pending';

  // Build a step-by-step view by joining fired events with the
  // snapshotted step list. Each event carries its `payload.stepIdx`
  // so we can match it to the right slot even if events are out of
  // order.
  const firedByIdx = new Map<number, IncidentEvent>();
  for (const ev of events) {
    if (ev.type !== 'escalation_step_fired') continue;
    const idx = (ev.payload?.stepIdx as number | undefined) ?? -1;
    if (idx >= 0) firedByIdx.set(idx, ev);
  }
  const nextStepIdx = status === 'in-progress' ? stepIdx + 1 : -1;

  return (
    <Card>
      <CardBody>
        <Stack gap={4}>
          <Row gap={2} justify="between" align="center">
            <Row gap={2} align="center">
              <Workflow size={14} strokeWidth={2} color="var(--color-accent)" />
              <span style={{ fontWeight: 'var(--fw-semibold)' }}>
                Escalation
              </span>
              {incident.escalationPolicyName && (
                <span className="ds-text-muted ds-text-sm">
                  · {incident.escalationPolicyName}
                </span>
              )}
            </Row>
            <Badge tone={tone} dot>{label}</Badge>
          </Row>

          <Row gap={6} align="center">
            <Stat
              label="Steps fired"
              value={`${firedCount} / ${stepCount}`}
              sub={
                status === 'in-progress'
                  ? 'chain running'
                  : status === 'halted'
                    ? 'stopped on ack / resolve'
                    : 'finished'
              }
            />
            {status === 'in-progress' && incident.nextEscalationAt && (
              <Stat
                label="Next step"
                value={fmtCountdown(incident.nextEscalationAt)}
                sub={
                  // Be honest about the polling cadence — actual fire
                  // can lag up to 60s past nextEscalationAt because the
                  // scheduler ticks every minute.
                  Date.now() >= incident.nextEscalationAt
                    ? 'fires at next poller tick (≤ 60s)'
                    : `due at ${formatTime(incident.nextEscalationAt)}`
                }
                live
              />
            )}
          </Row>

          {snapshot.length > 0 && (
            <Stack gap={2}>
              <span className="ds-label">Step timeline</span>
              <ol
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                {snapshot.map((step, idx) => (
                  <EscalationStepRow
                    key={idx}
                    idx={idx}
                    step={step}
                    firedEvent={firedByIdx.get(idx)}
                    isNext={idx === nextStepIdx}
                    isHalted={status === 'halted' && idx > stepIdx}
                  />
                ))}
              </ol>
            </Stack>
          )}

          {status === 'in-progress' && (
            <Row gap={1} align="center" className="ds-text-tertiary ds-text-xs">
              <Clock size={11} strokeWidth={2} />
              <span>Acknowledge to halt the chain — remaining steps won&apos;t fire.</span>
            </Row>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

/**
 * One row in the step timeline. Three visual states:
 *   fired   — green tick, "fired Xm ago", expanded target list with
 *             notification-channel chips (currently "Stubbed")
 *   next    — accent border + "fires in Xm", raw target list from
 *             snapshot (teams shown as teams, no member expansion)
 *   pending — muted, target list shown but no fire-time
 *   skipped — strikethrough'd, "halted before this step fired"
 */
function EscalationStepRow({
  idx,
  step,
  firedEvent,
  isNext,
  isHalted,
}: {
  idx: number;
  // Snapshotted step shape carries raw EscalationTarget[] (type+id+displayName).
  step: {
    delayMinutes: number;
    targets: Array<{ type: 'user' | 'team' | 'schedule'; id: string; displayName?: string }>;
  };
  firedEvent: IncidentEvent | undefined;
  isNext: boolean;
  isHalted: boolean;
}) {
  const stateColor = firedEvent
    ? 'var(--color-success)'
    : isNext
      ? 'var(--color-accent)'
      : isHalted
        ? 'var(--color-text-tertiary)'
        : 'var(--color-border-strong)';
  const stateBg = firedEvent
    ? 'var(--color-success-soft)'
    : isNext
      ? 'var(--color-accent-soft)'
      : 'transparent';

  // Pull fired-target details from the event payload. These are post-
  // expansion (teams already resolved to users), and each entry knows
  // whether it came from a direct mention or via a team.
  const firedTargets =
    (firedEvent?.payload?.targets as
      | Array<{
          userId: string;
          displayName: string;
          via: 'user' | 'team';
          viaTeamName?: string;
        }>
      | undefined) ?? [];

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'start',
        padding: 'var(--space-3)',
        background: stateBg,
        border: `1px solid ${isNext ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        opacity: isHalted ? 0.6 : 1,
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 'var(--radius-full)',
          background: stateColor,
          color: 'white',
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
        }}
        aria-hidden
      >
        {firedEvent ? '✓' : idx + 1}
      </div>

      <Stack gap={1}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-sm)' }}>
            Step {idx + 1}
          </span>
          {step.delayMinutes > 0 && (
            <span className="ds-text-tertiary ds-text-xs">
              · +{step.delayMinutes}m delay
            </span>
          )}
        </div>

        {firedEvent ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', rowGap: 'var(--space-1)' }}>
            {firedTargets.map((t) => (
              <Badge key={t.userId} tone="success">
                {t.displayName}
                {t.via === 'team' && t.viaTeamName && (
                  <span style={{ opacity: 0.7, marginLeft: 4 }}>
                    via {t.viaTeamName}
                  </span>
                )}
              </Badge>
            ))}
            <span className="ds-text-tertiary ds-text-xs" title="Notification channels are stubbed for chunk 8b; real SMS/email/push lands in chunk 10.">
              · channels stubbed
            </span>
          </div>
        ) : (
          // Not yet fired — show raw targets from the policy snapshot.
          // Team targets stay as teams (we don't pre-expand for preview
          // because the membership at fire time could differ).
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', rowGap: 'var(--space-1)' }}>
            {step.targets.length === 0 ? (
              <span className="ds-text-tertiary ds-text-xs">no targets configured</span>
            ) : (
              step.targets.map((t) => (
                <Badge key={`${t.type}:${t.id}`} tone="neutral">
                  {t.displayName ?? t.id}
                  {t.type === 'team' && (
                    <span style={{ opacity: 0.7, marginLeft: 4 }}>(team)</span>
                  )}
                  {t.type === 'schedule' && (
                    <span style={{ opacity: 0.7, marginLeft: 4 }}>(on-call)</span>
                  )}
                </Badge>
              ))
            )}
          </div>
        )}
      </Stack>

      <span className="ds-text-tertiary ds-text-xs ds-mono" style={{ whiteSpace: 'nowrap' }}>
        {firedEvent
          ? `${formatRelative(firedEvent.at)} ago`
          : isNext
            ? 'next (≤ 60s)'
            : isHalted
              ? 'skipped'
              : '—'}
      </span>
    </li>
  );
}

function formatRelative(ts: number): string {
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return `${diff}s`;
  const min = Math.floor(diff / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  return `${hr}h`;
}

/**
 * Countdown to the next escalation step. Honest about the engine's
 * minute-granularity polling — once the configured delay has elapsed,
 * the actual fire happens at the next poller tick (≤ 60s later).
 * Rather than showing "0s" for up to a minute (which looks broken),
 * flip to "due now" while the poller catches up.
 *
 * The Stat's sub-label below this number explains the polling cadence
 * to whoever's reading.
 */
function fmtCountdown(targetMs: number): string {
  const remaining = Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
  if (remaining === 0) return 'due now';
  return fmtDuration(remaining);
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}

/**
 * Maps incident.source to a human label for the "Open in X" button.
 * Falls back to a title-cased version of the source string for any
 * provider we haven't added a label for yet.
 */
function sourceLabel(source: string): string {
  if (source in PROVIDER_LABELS) {
    return PROVIDER_LABELS[source as IntegrationProvider];
  }
  return source.charAt(0).toUpperCase() + source.slice(1);
}
